import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  useSSE,
  type CallEvent,
  type FilEquipeEvent,
  type MentionEvent,
  type MessageCollegueEvent,
  type MessageCollegueLuEvent,
  type NePasDerangerEvent,
  type HoldClientHangupEvent,
  type TransfertAnnonceEvent,
  type SMSEvent,
  type VoicemailEvent,
} from '@/contexts/SSEContext'
import { useFirstCompany, useProfile } from '@/api/profile'
import { useCallStore } from '@/stores/useCallStore'
import { stopCallDurationTimer } from '@/lib/callTimer'
import { useUIStore } from '@/stores/useUIStore'
import { ouvrirCollegue, ouvrirConversation } from '@/lib/ouvrirConversation'
import { CLE_MESSAGES_COLLEGUES } from '@/api/messagesCollegues'
import { estIdentiteAgent } from '@/lib/identiteAgent'
import type { Voicemail } from '@/types/voicemails'
import type { Contact } from '@/types/contacts'

function findContactName(
  contacts: Contact[] | undefined,
  phone: string,
): string | null {
  if (!contacts) return null
  const stripped = phone.replace(/[\s\-()+]/g, '')
  for (const c of contacts) {
    if (!c.phone_number) continue
    const cStripped = c.phone_number.replace(/[\s\-()+]/g, '')
    if (cStripped === stripped) {
      return c.full_name?.trim() ||
        `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
        null
    }
  }
  return null
}

function isViewingThisSMS(phone: string): boolean {
  if (typeof window === 'undefined') return false
  if (document.visibilityState !== 'visible') return false
  const params = new URLSearchParams(window.location.search)
  const view = params.get('view') || 'phone'
  // La Boîte de réception sert 'phone' et l'ancienne vue 'sms'.
  if (view !== 'phone' && view !== 'sms') return false
  const ui = useUIStore.getState()
  if (params.get('tab') === 'appels') return ui.selectedPhoneForSms === phone
  return ui.selectedInboxPhone === phone
}

/** Le fil de ce collègue est ouvert et visible. */
function isViewingCollegue(userId: number): boolean {
  if (typeof window === 'undefined') return false
  if (document.visibilityState !== 'visible') return false
  const params = new URLSearchParams(window.location.search)
  if ((params.get('view') || 'phone') !== 'phone' || params.get('tab') === 'appels') return false
  return useUIStore.getState().selectedCollegueId === userId
}

/**
 * Hook qui branche les events SSE sur l'invalidation TanStack Query
 * et les notifications toast.
 *
 * À monter UNE seule fois dans le dashboard (typiquement dans le layout principal).
 *
 * Patterns utilisés :
 * - Events qui touchent des queries → setQueryData (optimistic) puis invalidateQueries
 * - Events qui touchent un store global (Twilio call) → action store directe
 * - Toast utilisateur pour les events humains (SMS reçu, voicemail nouveau)
 */
export function useSSEHandlers() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const {
    onSMS,
    onVoicemail,
    onVoicemailTranscribed,
    onCallUpdate,
    onHoldClientHangup,
    onTransfertAnnonce,
    onNoteInterne,
    onMention,
    onFilEquipe,
    onMessageCollegue,
    onMessageCollegueLu,
    onNePasDeranger,
  } = useSSE()
  const { data: company } = useFirstCompany()
  const companyId = company?.id ?? null
  const { data: profile } = useProfile()
  const moiId = profile?.id ?? null

  useEffect(() => {
    // ----- SMS reçu / envoyé -----
    const unsubSMS = onSMS((sms: SMSEvent) => {
      console.info('[SSE] sms event received', {
        id: sms.id,
        direction: sms.direction,
        from: sms.from_number,
        to: sms.to_number,
      })

      queryClient.invalidateQueries({ queryKey: ['sms-conversations'] })
      // Invalide TOUTES les queries sms-messages, peu importe la phoneNumber-key.
      // Robuste face aux mismatchs de format (E.164, sans +1, espaces…)
      queryClient.invalidateQueries({ queryKey: ['sms-messages'] })
      // Boîte de réception + fils unifiés (tous les numéros : même robustesse).
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
      queryClient.invalidateQueries({ queryKey: ['contact-timeline'] })

      if (sms.direction !== 'inbound' || sms.mise_a_jour) return
      // Si l'utilisateur regarde déjà cette conversation, pas besoin de toast
      if (isViewingThisSMS(sms.from_number)) return

      const contacts = queryClient.getQueryData<Contact[]>(['contacts', false])
      const contactName = findContactName(contacts, sms.from_number)
      const senderLabel = contactName || sms.from_number

      toast(t('sse.newSMS', 'Nouveau SMS'), {
        description: contactName
          ? `${contactName} · ${sms.from_number}`
          : t('sse.fromNumber', { number: senderLabel, defaultValue: `De ${senderLabel}` }),
        duration: 5000,
        action: {
          label: t('sse.reply', 'Répondre'),
          // Ouvre le fil de la personne dans la Boîte de réception.
          onClick: () => ouvrirConversation(sms.from_number),
        },
      })
    })

    // ----- Voicemail nouveau / mis à jour -----
    const unsubVoicemail = onVoicemail((vm: VoicemailEvent) => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
      queryClient.invalidateQueries({ queryKey: ['contact-timeline'] })
      const queryKey = ['voicemails', companyId]
      let isNew = false

      queryClient.setQueryData<Voicemail[]>(queryKey, (prev) => {
        if (!prev) {
          isNew = true
          return [
            {
              id: vm.id,
              from_number: vm.from_number,
              recording_url: vm.recording_url,
              recording_duration: vm.recording_duration,
              is_read: vm.is_read || vm.is_listened || false,
              status: vm.status,
              s3_available: vm.s3_available,
              created_at: vm.created_at,
              transcription: vm.transcription ?? null,
            },
          ]
        }
        const idx = prev.findIndex((v) => v.id === vm.id)
        if (idx >= 0) {
          const next = [...prev]
          next[idx] = {
            ...next[idx],
            recording_url: vm.recording_url,
            recording_duration: vm.recording_duration,
            is_read: vm.is_read || vm.is_listened || next[idx].is_read,
            status: vm.status,
            s3_available: vm.s3_available,
            created_at: vm.created_at,
          }
          return next
        }
        isNew = true
        return [
          {
            id: vm.id,
            from_number: vm.from_number,
            recording_url: vm.recording_url,
            recording_duration: vm.recording_duration,
            is_read: vm.is_read || vm.is_listened || false,
            status: vm.status,
            s3_available: vm.s3_available,
            created_at: vm.created_at,
            transcription: vm.transcription ?? null,
          },
          ...prev,
        ]
      })

      if (isNew) {
        toast(t('sse.newVoicemail') || 'Message vocal', {
          description: t('sse.fromNumber', { number: vm.from_number }) ||
            `De ${vm.from_number}`,
        })
      }
    })

    // ----- Voicemail transcrit (asynchrone, pas de toast) -----
    const unsubVMTranscribed = onVoicemailTranscribed((data: VoicemailEvent) => {
      queryClient.invalidateQueries({ queryKey: ['contact-timeline'] })
      queryClient.setQueryData<Voicemail[]>(['voicemails', companyId], (prev) => {
        if (!prev) return prev
        return prev.map((v) =>
          v.id === data.id ? { ...v, transcription: data.transcription ?? null } : v,
        )
      })
    })

    // ----- Call update (started, completed, missed, etc.) -----
    const unsubCall = onCallUpdate((call: CallEvent) => {
      // L'invalidation refetch toutes les pages de l'infinite query
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
      queryClient.invalidateQueries({ queryKey: ['contact-timeline'] })
      // Appel interne : il apparaît dans le fil des deux collègues.
      if (estIdentiteAgent(call.from_number) && estIdentiteAgent(call.to_number)) {
        queryClient.invalidateQueries({ queryKey: CLE_MESSAGES_COLLEGUES })
      }
    })

    // ----- Client raccroché pendant le hold (Twilio) -----
    const unsubHold = onHoldClientHangup((event: HoldClientHangupEvent) => {
      // Ne pas afficher d'alarme si l'agent n'est PAS / PLUS en hold :
      //   - hold pas en cours → faux positif (le backend peut envoyer l'event sur
      //     un disconnect leg agent normal lors du hold initial)
      //   - reprise réussie (isCallConnected === true) → l'agent a déjà repris,
      //     l'info "client raccroché" est obsolète
      const callStore = useCallStore.getState()
      if (!callStore.isOnHold || callStore.isCallConnected) {
        void event
        return
      }

      callStore.setIsOnHold(false)
      callStore.setIsHoldLoading(false)
      callStore.reset()

      toast.error(t('phone.clientHungUpOnHold', 'Client raccroché'), {
        description: t(
          'phone.clientHungUpOnHoldDesc',
          'Le correspondant a raccroché pendant la mise en attente.',
        ),
        duration: 5000,
      })

      void event
    })

    // ----- Announced transfer: the colleague and the caller, as they come -----
    const unsubAnnonce = onTransfertAnnonce((e: TransfertAnnonceEvent) => {
      const store = useCallStore.getState()
      const annonce = store.annonce
      // The colleague who took the caller holds it in the same conference
      // without knowing its name: the server tells who holds the call now.
      const finDuClient = e.evenement === 'client_parti' || e.evenement === 'fin'
      if (finDuClient && !annonce && e.user_id !== null && e.user_id === moiId) {
        if (store.isOnHold) {
          store.currentCall?.disconnect()
          stopCallDurationTimer()
          store.reset()
          toast.error(t('phone.clientHungUpOnHold', 'Client raccroché'))
        }
        return
      }
      if (!annonce || annonce.conference !== e.conference) return
      const name = e.label || annonce.label
      const sansAnnonce = () => useCallStore.setState({ annonce: { ...annonce, etape: null } })
      // Who is in the call with the caller and this agent, after this step.
      if (e.participants) useCallStore.setState({ participants: e.participants })
      switch (e.evenement) {
        case 'collegue_en_ligne':
          useCallStore.setState({ annonce: { ...annonce, etape: 'en_ligne' } })
          break
        case 'collegue_absent':
          sansAnnonce()
          toast(t('toasts.call.colleagueNoAnswer', { name }))
          break
        case 'collegue_parti':
          sansAnnonce()
          toast(t('toasts.call.colleagueHungUp', { name }))
          break
        case 'collegue_injoignable':
        case 'echec':
          sansAnnonce()
          toast.error(t('toasts.call.announceFailed'))
          break
        case 'annule':
        case 'passe':
        case 'reuni':
          // Handed over: the server removes this leg, its disconnect closes the screen.
          // Merged: the announcement ends, everyone talks together.
          sansAnnonce()
          break
        case 'invite_appele':
          useCallStore.setState({ ajoutEnCours: null })
          break
        case 'invite_absent':
          toast(t('toasts.call.inviteNoAnswer', { name }))
          break
        case 'invite_injoignable':
          useCallStore.setState({ ajoutEnCours: null })
          toast.error(t('toasts.call.inviteUnreachable', { name }))
          break
        case 'client_parti':
        case 'fin':
          // The caller is gone. A leg on hold or still to come would never
          // report it: close the call screen here.
          if (store.isOnHold || store.retourAttendu) {
            store.currentCall?.disconnect()
            stopCallDurationTimer()
            store.reset()
            toast.error(t('phone.clientHungUpOnHold', 'Client raccroché'))
          }
          break
      }
    })

    // ----- Collaboration d'équipe (plan Team) -----
    // Une note publiée par un collègue : le fil se recharge.
    const unsubNote = onNoteInterne(() => {
      queryClient.invalidateQueries({ queryKey: ['contact-timeline'] })
      // A comment on a call or a text: its open discussion follows.
      queryClient.invalidateQueries({ queryKey: ['discussion'] })
    })

    // « X vous a mentionné » : seulement pour la personne citée.
    const unsubMention = onMention((m: MentionEvent) => {
      if (moiId === null || m.to_user_id !== moiId) return
      // La pastille « @ » apparaît dans la liste.
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
      toast(t('inbox.team.mentionedYou', '{{name}} vous a mentionné', { name: m.by_name }), {
        description: m.text,
        duration: 8000,
        action: {
          label: t('inbox.team.open', 'Ouvrir'),
          onClick: () => ouvrirConversation(m.phone, m.line),
        },
      })
    })

    // Attribution, Terminé, Rouvert : la liste et l'en-tête se mettent à jour ;
    // la personne à qui on attribue la conversation est prévenue.
    const unsubFil = onFilEquipe((f: FilEquipeEvent) => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
      queryClient.invalidateQueries({ queryKey: ['inbox-state', f.phone] })
      if (f.action !== 'assign' || moiId === null) return
      if (f.assigned_to_user_id !== moiId || f.by_user_id === moiId) return
      toast(t('inbox.team.assignedYou', '{{name}} vous a attribué une conversation', { name: f.by_name }), {
        description: f.phone,
        duration: 8000,
        action: {
          label: t('inbox.team.open', 'Ouvrir'),
          onClick: () => ouvrirConversation(f.phone, f.line),
        },
      })
    })

    // ----- Messages entre collègues -----
    // Les deux personnes rechargent ; le destinataire est prévenu s'il ne
    // regarde pas déjà ce fil.
    const unsubMessageCollegue = onMessageCollegue((m: MessageCollegueEvent) => {
      if (moiId === null || (m.to_user_id !== moiId && m.by_user_id !== moiId)) return
      queryClient.invalidateQueries({ queryKey: CLE_MESSAGES_COLLEGUES })
      if (m.to_user_id !== moiId || isViewingCollegue(m.by_user_id)) return
      toast(t('messagesCollegues.nouveau', '{{name}} vous a écrit', { name: m.by_name }), {
        duration: 6000,
        action: {
          label: t('inbox.team.open', 'Ouvrir'),
          onClick: () => ouvrirCollegue(m.by_user_id),
        },
      })
    })
    // Lu : la pastille disparaît dans les autres onglets, l'auteur voit « Lu ».
    const unsubMessageCollegueLu = onMessageCollegueLu((l: MessageCollegueLuEvent) => {
      if (moiId === null || (l.user_id !== moiId && l.collegue_user_id !== moiId)) return
      queryClient.invalidateQueries({ queryKey: CLE_MESSAGES_COLLEGUES })
    })
    // Do not disturb changed elsewhere (phone, profile, another tab): the dot follows the server.
    // The profile is not refetched: it would wipe what is being typed in My Profile.
    const unsubNePasDeranger = onNePasDeranger((e: NePasDerangerEvent) => {
      queryClient.setQueryData(['agent-status'], e.status)
    })

    return () => {
      unsubMessageCollegue()
      unsubMessageCollegueLu()
      unsubNePasDeranger()
      unsubNote()
      unsubMention()
      unsubFil()
      unsubSMS()
      unsubVoicemail()
      unsubVMTranscribed()
      unsubCall()
      unsubHold()
      unsubAnnonce()
    }
  }, [
    onSMS,
    onVoicemail,
    onVoicemailTranscribed,
    onCallUpdate,
    onHoldClientHangup,
    onTransfertAnnonce,
    onNoteInterne,
    onMention,
    onFilEquipe,
    onMessageCollegue,
    onMessageCollegueLu,
    onNePasDeranger,
    queryClient,
    companyId,
    moiId,
    t,
  ])
}
